import { Root } from "@/models/roots/root.model";
import { ComponentCampaignDTO } from "@/pages/ladle-track/ladle-details/models/component-campaign.dto";
import {
	EAF,
	LadlePlantOverviewDTO,
	Location as LocationFromLadlesDto,
} from "@/pages/ladle-track/plant-overview/models/ladle-plant-overview.dto";
import { getError } from "@/utils/get-error";
import { axiosInstance, getMomentTz } from "@dexteel/mesf-core";
import { AxiosInstance } from "axios";

type changeRootLocationArgs = {
	rootId: number | null;
	locationId: number | null;
};
const moment = getMomentTz();

export type LadleTapTempAlertConfiguration = {
	ladleTapTempAlertConfigurationId: number;
	alarmCode: string;
	threshold: number;
	isActive: boolean;
	sendEmailNotifications: boolean;
	notificationEmails: string | null;
	updateTimestamp: string | null;
	updatedBy: string | null;
};

export class LadlesRepository {
	async getLadles(
		isTracked: boolean | null = null,
	): Promise<{ error: boolean; data: Root[] }> {
		const resp = await this.getAxiosInstance().get("/ladle", {
			params: {
				isTracked,
			},
		});

		if (resp.data && Array.isArray(resp.data)) {
			resp.data = resp.data.map((ladle: Root) => ({
				...ladle,
				currentHeat: ladle.currentHeat
					? {
							...ladle.currentHeat,
							eafStartTime: ladle.currentHeat.eafStartTime
								? moment.utc(ladle.currentHeat.eafStartTime).toDate()
								: null,
							tapStartTime: ladle.currentHeat.tapStartTime
								? moment.utc(ladle.currentHeat.tapStartTime).toDate()
								: null,
							ladleOpenTime: ladle.currentHeat.ladleOpenTime
								? moment.utc(ladle.currentHeat.ladleOpenTime).toDate()
								: null,
							ladleCloseTime: ladle.currentHeat.ladleCloseTime
								? moment.utc(ladle.currentHeat.ladleCloseTime).toDate()
								: null,
							reladledTime: ladle.currentHeat.reladledTime
								? moment.utc(ladle.currentHeat.reladledTime).toDate()
								: null,
					  }
					: null,
				components: ladle.components?.map((component) => ({
					...component,
					lastTask: component.lastTask
						? {
								...component.lastTask,
								performTime: component.lastTask.performTime
									? moment.utc(component.lastTask.performTime).toDate()
									: null,
						  }
						: null,
				})),
			}));
		}

		return {
			error: false,
			data: resp.data,
		};
	}

	async getLadlesWithComponents(withComponents: boolean = true): Promise<
		| { error: false; data: Root[] }
		| {
				error: true;
				message: string;
		  }
	> {
		try {
			const resp = await this.getAxiosInstance().get("/ladle", {
				params: {
					with: withComponents ? "components" : null,
				},
			});

			if (resp.data && Array.isArray(resp.data)) {
				resp.data = resp.data.map((ladle: Root) => ({
					...ladle,
					currentHeat: ladle.currentHeat
						? {
								...ladle.currentHeat,
								eafStartTime: ladle.currentHeat.eafStartTime
									? moment.utc(ladle.currentHeat.eafStartTime).toDate()
									: null,
								tapStartTime: ladle.currentHeat.tapStartTime
									? moment.utc(ladle.currentHeat.tapStartTime).toDate()
									: null,
								ladleOpenTime: ladle.currentHeat.ladleOpenTime
									? moment.utc(ladle.currentHeat.ladleOpenTime).toDate()
									: null,
								ladleCloseTime: ladle.currentHeat.ladleCloseTime
									? moment.utc(ladle.currentHeat.ladleCloseTime).toDate()
									: null,
								reladledTime: ladle.currentHeat.reladledTime
									? moment.utc(ladle.currentHeat.reladledTime).toDate()
									: null,
						  }
						: null,
					components: ladle.components?.map((component) => ({
						...component,
						lastTask: component.lastTask
							? {
									...component.lastTask,
									performTime: component.lastTask.performTime
										? moment.utc(component.lastTask.performTime).toDate()
										: null,
							  }
							: null,
					})),
				}));
			}

			return {
				error: false,
				data: resp.data,
			};
		} catch (e: any) {
			return {
				error: true,
				message: getError(e),
			};
		}
	}

	async getParts(componentTypeId: number) {
		const resp = await this.getAxiosInstance().get("/root/parts", {
			params: {
				componentTypeId,
			},
		});
		return {
			error: false,
			data: resp.data,
		};
	}

	async getLadlePlantOverview(): Promise<
		| { error: false; data: LadlePlantOverviewDTO | string }
		| {
				error: true;
				message: string;
		  }
	> {
		try {
			const resp = await this.getAxiosInstance().get<LadlePlantOverviewDTO>(
				"/ladle/plant-overview",
			);

			if (resp.data) {
				const parsedData: LadlePlantOverviewDTO = {
					eaFs: Object.fromEntries(
						Object.entries(resp.data.eaFs).map(
							([key, eaf]: [string, EAF]) => [
								key,
								{
									...eaf,
									eafStartTime: eaf.eafStartTime
										? moment.utc(eaf.eafStartTime).toDate()
										: null,
								},
							],
						),
					),
					locations: Object.fromEntries(
						Object.entries(resp.data.locations).map(
							([key, location]: [string, LocationFromLadlesDto]) => [
								key,
								{
									...location,
									root: location.root
										? {
												...location.root,
												turnaroundDateTime: location.root.turnaroundDateTime
													? moment
															.utc(location.root.turnaroundDateTime)
															.toDate()
													: null,
										  }
										: null,
								},
							],
						),
					),
				};

				return {
					error: false,
					data: parsedData,
				};
			}

			return {
				error: false,
				data: resp.data,
			};
		} catch (e: any) {
			return {
				error: true,
				message: getError(e),
			};
		}
	}

	async getRootDetails(rootId: number) {
		const resp = await this.getAxiosInstance().get("/ladle/details", {
			params: {
				rootId,
			},
		});
		return {
			error: false,
			data: resp.data,
		};
	}

	async changeRootLocation({
		rootId = null,
		locationId,
	}: changeRootLocationArgs) {
		const resp = await this.getAxiosInstance().patch(
			`/ladle/location`,
			{},
			{
				params: {
					rootId,
					locationId,
				},
			},
		);
		return resp.data;
	}

	async setRootState(data: setRootStateArgs) {
		const resp = await this.getAxiosInstance().patch(
			`/Ladle/status`,
			{},
			{
				params: {
					...data,
					operatorComments: data.workerComments,
				},
			},
		);
		if (resp.data.success) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return Promise.reject(resp.data);
		}
	}

	async getSpecialComponentsListForTundishId(tundishId: number) {
		const resp = await this.getAxiosInstance().get(
			`/tundish/${tundishId}/special-components`,
		);
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getCampaignsForComponent(
		ladleId: number,
		componentTypeId: number,
		params: { [key: string]: any } = {},
	) {
		const resp = await this.getAxiosInstance().get<ComponentCampaignDTO[]>(
			`/ladle/${ladleId}/components/${componentTypeId}/campaigns`,
			{
				params: {
					...params,
				},
			},
		);
		if (resp.status === 200) {
			const parsedData = resp.data.map((campaign) => ({
				...campaign,
				installTime: moment.utc(campaign.installTime).toDate(),
				removeTime: campaign.removeTime
					? moment.utc(campaign.removeTime).toDate()
					: null,
			}));
			return {
				error: false,
				data: parsedData,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getLadleTGIs(ladleId: number, params: { [key: string]: any } = {}) {
		try {
			const resp = await this.getAxiosInstance().get(`/Ladle/${ladleId}/tgis`, {
				params: {
					...params,
				},
			});
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e) {
			return {
				ok: false,
				message: getError(e),
			};
		}
	}

	async getTundishTasks(ladleId: number) {
		const resp = await this.getAxiosInstance().get(`/tundish/${ladleId}/tasks`);
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getLadleHeats(ladleId: number, params: { [key: string]: any } = {}) {
		const resp = await this.getAxiosInstance().get(`/Ladle/${ladleId}/heats`, {
			params: {
				...params,
			},
		});
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getCampaignList() {
		const resp = await this.getAxiosInstance().get(`/Ladle/campaigns`);
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getLadleCampaigns(params: { [key: string]: any } = {}) {
		try {
			const resp = await this.getAxiosInstance().get(`/Ladle/ladle-campaigns`, {
				params: {
					...params,
				},
			});
			return {
				error: false as const,
				data: resp.data,
			};
		} catch (e: any) {
			return {
				error: true as const,
				message: getError(e),
			};
		}
	}

	async getTundishBuildLog(filters: {
		startDateTime?: string;
		endDateTime?: string;
		rootId?: number;
		tundishes?: string[];
		reverseSort: boolean;
	}) {
		const resp = await this.getAxiosInstance().get(`/tundish/build-log`);
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getHeatEventsForLadle(
		ladleId: number,
		params: { [key: string]: any } = {},
	) {
		const resp = await this.getAxiosInstance().get(`/heat/events-by-ladle`, {
			params: {
				rootId: ladleId,
				start: params.from || null,
				end: params.to || null,
			},
		});
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getRootEvents(params: any) {
		const resp = await this.getAxiosInstance().get(`/root/events`, {
			params: {
				...params,
			},
		});
		if (resp.status === 200) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return {
				ok: false,
				data: resp.data,
			};
		}
	}

	async getComponentsForLadle(ladleId: number) {
		const resp = await this.getAxiosInstance().get(
			`/ladle/${ladleId}/components`,
		);
		if (resp.status === 200) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return {
				ok: false,
				data: resp.data,
			};
		}
	}

	async getTapTempAlertConfiguration() {
		const resp =
			await this.getAxiosInstance().get<LadleTapTempAlertConfiguration>(
				"/ladle/tap-temp-alert-configuration",
			);
		return resp.data;
	}

	async updateTapTempAlertConfiguration(
		data: Partial<LadleTapTempAlertConfiguration>,
	) {
		const resp =
			await this.getAxiosInstance().patch<LadleTapTempAlertConfiguration>(
				"/ladle/tap-temp-alert-configuration",
				data,
			);
		return resp.data;
	}

	private getAxiosInstance(): AxiosInstance {
		return axiosInstance;
	}
}

export interface setRootStateArgs {
	heatName: string;
	needsAttention: boolean;
	attentionComments: string;
	rootStateId: number;
	rootId: number;
	workerComments: string;
	stateComments: string;
	supervisorInstructions: string;
	locationId?: number;
	slideGateLeftOpenForPreheat: boolean;
}

export const ladlesRepository = new LadlesRepository();
